import { currentUserQueryOptions, guest, type User } from '#shared'
import { useQuery, useQueryClient, type InfiniteData } from '@tanstack/vue-query'
import { computed, onBeforeMount, onBeforeUnmount, unref } from 'vue'
import {
  getChatQueryKey,
  PERSONAL_CHATS_QUERY_KEY,
  PUBLIC_CHATS_QUERY_KEY,
  type Chat,
  type ChatMeta,
} from '#entities/chat'
import type { GetChatsResponse } from '#entities/chat/model/get-chats-response'
import { wsClient } from '#shared/api/ws-client'
import type { Message } from '../model/message'
import { type MessageEvent, type MessageEventOperation } from '../model/message-event'
import type { MessagesQueryData } from '../model/messages-query-data'
import { appendMessage } from './append-message'
import { deleteMessage } from './delete-message'
import { getFreshMessagesQueryKeyGlobalPrefix } from './fresh-messages-query'
import { getMessagesQueryKeyGlobalPrefix } from './messages-query'
import { updateMessage } from './update-message'

export function useDynamicMessageUpdates() {
  const queryClient = useQueryClient()

  const currentUserQuery = useQuery(currentUserQueryOptions)
  const currentUser = currentUserQuery.data
  const currentUserId = computed(() => {
    const currentUserVal = unref(currentUser)
    if (currentUserVal === guest) return null
    return currentUserVal?.id ?? null
  })

  const increaseUnreadCount = <CurrentOperation extends MessageEventOperation>(
    messageEvent: MessageEvent<CurrentOperation>
  ) => {
    const updateChat = (chat: Chat) => {
      const newUnreadCount = (chat.unreadCount ?? 0) + 1
      const newChatMeta: ChatMeta = { ...chat.meta }
      if (!chat.meta?.unreadStartsAt) {
        newChatMeta.unreadStartsAt = new Date()
      }

      return {
        ...chat,
        unreadCount: newUnreadCount,
        lastMessage: messageEvent.message as Message,
        meta: newChatMeta,
      }
    }

    queryClient.setQueryData(PUBLIC_CHATS_QUERY_KEY, (queryData: GetChatsResponse | undefined) => {
      if (!queryData) return

      const newChats = queryData.data.map(chat => {
        if (chat.id !== messageEvent.chatId) return chat
        return updateChat(chat)
      })

      return {
        ...queryData,
        data: newChats,
      }
    })

    queryClient.setQueryData(PERSONAL_CHATS_QUERY_KEY, (queryData: InfiniteData<GetChatsResponse> | undefined) => {
      if (!queryData) return

      const newPages = queryData.pages.map(page => {
        const newChats = page.data.map(chat => {
          if (chat.id !== messageEvent.chatId) return chat
          return updateChat(chat)
        })

        return {
          ...page,
          data: newChats,
        }
      })

      return {
        ...queryData,
        pages: newPages,
      }
    })

    queryClient.invalidateQueries({ queryKey: getChatQueryKey(messageEvent.chatId) })
  }

  const updateUserInMessage = (message: Message, user: User) => {
    const newMessage = { ...message }
    let isMutated = false

    if (message.sender.id === user.id) {
      newMessage.sender = { ...message.sender, ...user }
      isMutated = true
    }

    if (user.login in (message.pings ?? {})) {
      message.pings![user.login] = { ...message.pings![user.login], ...user }
      isMutated = true
    }

    if (isMutated) return newMessage
    return message
  }

  const updateUserInMessages = (user: User) => {
    queryClient.setQueriesData(
      { queryKey: getMessagesQueryKeyGlobalPrefix() },
      (queryData: MessagesQueryData | undefined) => {
        if (!queryData) return

        const newPages = queryData.pages.map(page => ({
          ...page,
          data: page.data.map(existingMessage => updateUserInMessage(existingMessage, user)),
        }))

        return {
          ...queryData,
          pages: newPages,
        }
      }
    )

    queryClient.setQueriesData(
      { queryKey: getFreshMessagesQueryKeyGlobalPrefix() },
      (queryData: Message[] | undefined) => {
        if (!queryData) return
        return queryData.map(existingMessage => updateUserInMessage(existingMessage, user))
      }
    )
  }

  const onMessageEvent = <CurrentOperation extends MessageEventOperation>(
    messageEvent: MessageEvent<CurrentOperation>
  ) => {
    if (!messageEvent.message.clientId) {
      messageEvent.message.clientId = messageEvent.clientId
    }

    messageEvent.message.meta = {
      ...messageEvent.message.meta,
      isFresh: true,
    }

    if (messageEvent.operation === 'create') {
      appendMessage(queryClient, messageEvent.message as Message)

      if (messageEvent.message.sender?.id !== currentUserId.value) {
        increaseUnreadCount(messageEvent)
      }
    }

    if (messageEvent.operation === 'update') {
      updateMessage(queryClient, messageEvent.message as Message)
    }

    if (messageEvent.operation === 'delete') {
      deleteMessage(queryClient, messageEvent.message as Message)
    }

    if ('sender' in messageEvent.message) updateUserInMessages(messageEvent.message.sender!)
  }

  onBeforeMount(() => {
    wsClient.on('message', onMessageEvent)
  })

  onBeforeUnmount(() => {
    wsClient.off('message', onMessageEvent)
  })
}
